/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import isEmpty from 'lodash/isEmpty';

import { fplsFetch, logErrorToCloudReporting, resetNewOptions, generateActiveTheme, applyTheme } from '../../../shared/utils';
import { updateFormInventoryProducts } from '../../builder/slice-actions/form';
import { getPipedCtrlLabelsMap, formAmendForm } from '../../../shared/form-lib';
import blankDesignTheme from './default-styles';
import defaultDesign from './default-design';

export const defaultState = {
  form: {},
  theme: {},
  error: '',
  loading: true,
  googleFonts: [],
  activeTheme: [],
  customiseForm: {},
  actualView: false,
  themeTemplates: [],
  loadingFonts: true,
  userCustomTexts: {},
  isCustomising: false,
  loadingFontsError: '',
  reportCustomise: false,
  themeTemplatesError: '',
  showMobileSideBar: false,
  labelPipedCtrlLabelsMap: {},
};

function amendThemeArray(version3) {
  // filtering unpublished theme
  const newVersion3 = version3.filter((theme) => theme.published);
  newVersion3.unshift(blankDesignTheme);

  return newVersion3;
}

const notFoundMsg = 'Requested form could not be found.';
const failedGetMsg =
  'An error has occurred when attempting to ' +
  'retrieve this form. Please refresh & try again. You may also send ' +
  'an email to support@formpl.us if this persists';

export const fetchFormById = createAsyncThunk('customise/fetchFormById', async (formId) => {
  try {
    const response = await fplsFetch({ url: `/api/v2/user/form/${formId}` });
    return await updateFormInventoryProducts(response, formId);
  } catch (err) {
    console.error(err, 'error');
  }
});

export const fetchThemes = createAsyncThunk('customise/fetchThemes', async () => {
  const response = await fplsFetch({ url: '/api/v2/user/csstemplates?version=3' });
  return response;
});

export const fetchFonts = createAsyncThunk('customise/fetchFonts', async () => {
  try {
    const response = await fetch(
      'https://www.googleapis.com/webfonts/v1/webfonts?sort=popularity&key=AIzaSyDNpMvZXvwITG-x3W3yOhQPjV89Io2Z6WI'
    );
    const fonts = await response.json();
    const googleFonts = fonts.items.slice(0, 100).map((font) => ({
      text: font.family,
      label: font.family,
      value: font.family,
    }));

    return googleFonts;
  } catch (error) {
    console.error(error);
    return error.message;
  }
});

const customiseSlice = createSlice({
  name: 'customise',
  initialState: defaultState,
  reducers: {
    doApplyTheme: (state, { payload }) => {
      const { themeName } = payload;
      const { customiseForm, themeTemplates } = state;
      window.mixpanel.track('Changed Form Theme', {
        themeName,
      });
      const selectedTheme = themeTemplates.find((theme) => theme.name === themeName);
      applyTheme(selectedTheme, customiseForm);
      customiseForm.design.theme = themeName;
    },
    updateFormOptionsSuccess: (state) => {
      state.isCustomising = false;
    },
    updateFormOptionsFailure: (state) => {
      return {
        ...state,
        customiseForm: state.form,
      };
    },
    updateCustomiseForm: (state, { payload }) => {
      const { customiseForm } = state;
      const { designCategory, designProp, value } = payload;
      if (designCategory === 'old') {
        customiseForm[designProp] = value;
      } else {
        customiseForm.design[designProp] = value;
      }
      state.isCustomising = true;
    },
    doResetCustomiseForm: (state) => {
      const { customiseForm, themeTemplates } = state;
      const themeName = customiseForm.design?.theme;
      if (themeName !== 'None') {
        // revert to original theme default
        const presentTheme = themeTemplates.find((theme) => theme.name === themeName);
        applyTheme(presentTheme, customiseForm);
      } else {
        // revert to blank design
        resetNewOptions(customiseForm);
        customiseForm.design.theme = 'None';
      }
      state.isCustomising = true;
    },
    displayMobileSidebar: (state, { payload }) => {
      state.showMobileSideBar = payload;
    },
    showReportFormInCustomise: (state, { payload }) => {
      state.reportCustomise = payload;
    },
    resetPipedCtrlLabelsMap: (state) => {
      state.labelPipedCtrlLabelsMap = defaultState.labelPipedCtrlLabelsMap;
    }
  },

  // Api calls for customise page go here
  extraReducers: (customise) => {
    customise.addCase(fetchFormById.pending, (state) => {
      state.loading = true;
    });
    customise.addCase(fetchFormById.fulfilled, (state, { payload }) => {
      const { themeTemplates } = state;
      state.loading = false;
      const form = formAmendForm(payload);

      state.form = form;
      state.customiseForm = form;
      state.userCustomTexts = payload.user_custom_texts;
      
      state.labelPipedCtrlLabelsMap = getPipedCtrlLabelsMap(form?.definition?.pages ?? [], true);

      if (!form.design) {
        state.form.design = defaultDesign;
        state.customiseForm.design = defaultDesign;
      }

      if (!isEmpty(themeTemplates)) {
        const activeTheme = generateActiveTheme(form.design, themeTemplates);
        state.activeTheme = activeTheme;
      }

      if (payload.error) {
        const error = payload.error && payload.status === 404 ? notFoundMsg : failedGetMsg;
        state.error = error;
        console.error('Fetch Form Error', error);
        logErrorToCloudReporting(error);
        toast.error(error);
      }
    });
    customise.addCase(fetchFormById.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    });

    customise.addCase(fetchThemes.fulfilled, (state, { payload }) => {
      const { customiseForm } = state;
      if (payload.error) {
        state.themeTemplatesError = payload.error;
        console.error('Fetch Themes Error', payload.error);
      } else {
        const { version3 } = payload;
        const themeTemplates = amendThemeArray(version3);
        const activeTheme = generateActiveTheme(customiseForm.design, themeTemplates);
        state.activeTheme = activeTheme;
        state.themeTemplates = themeTemplates;
      }
    });

    customise.addCase(fetchFonts.pending, (state) => {
      state.loadingFonts = true;
    });
    customise.addCase(fetchFonts.fulfilled, (state, { payload }) => {
      state.loadingFonts = false;
      state.googleFonts = payload;
    });
    customise.addCase(fetchFonts.rejected, (state, { payload }) => {
      state.loadingFonts = false;
      state.fontError = payload.error.message;
    });
  },
});
export const {
  doApplyTheme,
  updateCustomiseForm,
  doResetCustomiseForm,
  displayMobileSidebar,
  resetPipedCtrlLabelsMap,
  updateFormOptionsFailure,
  updateFormOptionsSuccess,
  showReportFormInCustomise,
} = customiseSlice.actions;
export default customiseSlice.reducer;
